import React, { useState } from 'react';
import {
  Button,
  Chip,
  Menu,
  MenuItem,
  Paper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';

const ResultsTableToolbar = ({
  rowCount,
  selectedBrandCodes,
  onClearBrands,
  matchedTermLegends,
  selectedRowsCount,
  onOpenCompare,
  onCopyTsv,
  onCopyJson,
  onExportCsv,
}) => {
  const [copyAnchorEl, setCopyAnchorEl] = useState(null);
  const copyMenuOpen = Boolean(copyAnchorEl);
  const copyDisabled = selectedRowsCount === 0;
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportAnchorEl);

  const handleCopyMenuOpen = (event) => {
    if (copyDisabled) return;
    setCopyAnchorEl(event.currentTarget);
  };

  const handleCopyMenuClose = () => {
    setCopyAnchorEl(null);
  };

  const handleExportMenuOpen = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportAnchorEl(null);
  };

  const handleCopyTsv = () => {
    onCopyTsv?.();
    handleCopyMenuClose();
  };

  const handleCopyJson = () => {
    onCopyJson?.();
    handleCopyMenuClose();
  };

  const handleExportCsv = () => {
    onExportCsv?.();
    handleExportMenuClose();
  };

  const matchedLegendChips = matchedTermLegends.map(({ index, term, color }) => (
    <Chip
      key={`matched-term-legend-${index}`}
      label={term}
      size="small"
      data-testid={`results-matched-term-legend-${index}`}
      sx={{
        backgroundColor: alpha(color, 0.14),
        borderColor: alpha(color, 0.45),
        borderWidth: 1,
        borderStyle: 'solid',
      }}
    />
  ));

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1,
        p: 1.25,
        borderRadius: 3,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Chip label={`Rows: ${rowCount}`} color="primary" variant="outlined" size="small" />
      {selectedBrandCodes.length > 0 ? (
        <Chip
          label={`Brands: ${selectedBrandCodes.join(', ')}`}
          onDelete={onClearBrands}
          variant="outlined"
          size="small"
        />
      ) : null}
      {matchedLegendChips}

      <Button
        size="small"
        variant="outlined"
        startIcon={<CompareArrowsIcon />}
        onClick={onOpenCompare}
        disabled={selectedRowsCount < 2}
        aria-label="Compare selected"
        data-testid="results-compare"
      >
        Compare
      </Button>

      <Button
        size="small"
        variant="outlined"
        startIcon={<ContentCopyIcon />}
        onClick={handleCopyMenuOpen}
        disabled={copyDisabled}
        aria-label="Copy selected"
        data-testid="results-copy"
      >
        Copy
      </Button>
      <Menu
        anchorEl={copyAnchorEl}
        open={copyMenuOpen}
        onClose={handleCopyMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleCopyTsv} disabled={copyDisabled}>
          Copy Selected TSV
        </MenuItem>
        <MenuItem onClick={handleCopyJson} disabled={copyDisabled}>
          Copy Selected JSON
        </MenuItem>
      </Menu>

      <Button
        size="small"
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={handleExportMenuOpen}
        aria-label="Export options"
        data-testid="results-export"
      >
        Export
      </Button>
      <Menu
        anchorEl={exportAnchorEl}
        open={exportMenuOpen}
        onClose={handleExportMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleExportCsv}>Export CSV</MenuItem>
      </Menu>
    </Paper>
  );
};

export default ResultsTableToolbar;
